/* =========================================================================
 * /docs/how — methodology page.
 *
 * Rewritten as part of the 2026 redesign. The previous version pitched the
 * crypto/NFT purchase flow as the main story; this one focuses on the
 * actual data pipeline (capture → epochs → uploads → areas → datasets)
 * and the privacy boundaries that hold all the way through.
 * ========================================================================= */

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import SiteFooter from "components/SiteFooter";

import consumerRoutes from "consumerRoutes";
import footerRoutes from "footer.routes";

const STAGES = [
  {
    icon: "smartphone",
    title: "Capture",
    body: "Each device samples sensors at 1 Hz while a contribution session is active. The user picks an activity mode (walking, driving, flying, etc.) on start — every reading carries that tag.",
    detail: [
      "Location is the epoch driver — when GPS isn't producing fixes, other sensors check and drop their samples.",
      "Per-sensor input filters strip noise on-device: GPS jumps over 200 m/s are rejected; accelerometer shake events apply a 5-second cooldown.",
      "Sound is captured as amplitude only — never as audio.",
    ],
  },
  {
    icon: "layers",
    title: "Epoch & batch",
    body: "Every 20 location samples (~20 seconds of activity), the device closes an epoch and queues an upload. All other sensors that produced data during that window are bundled with it.",
    detail: [
      "An epoch is the unit of upload — never a single reading. This keeps coordinates fuzzy enough that no individual moment-in-time is identifying.",
      "Each upload includes the lat/lng of the start and end of the epoch plus a downsampled path; the per-second raw track stays on the device unless explicitly licensed.",
    ],
  },
  {
    icon: "cloud_upload",
    title: "Upload",
    body: "The batch is written to Cloud Storage as a JSON file, and a summary doc is written to Firestore at devices/{anonId}/location/{hash}. The hash is content-addressed — same input, same hash — so duplicate work is naturally deduplicated.",
    detail: [
      "Device identity is an anonymous Firebase Auth UID. No email, no phone number, no name.",
      "Uploads are queued via WorkManager — they survive network drops and resume cleanly.",
    ],
  },
  {
    icon: "public",
    title: "Aggregate",
    body: "A Cloud Function assigns each upload to a 250 km contribution area. Areas are public-readable; individual device tracks are not. Researchers see density, coverage, and activity — never any one contributor's movements.",
    detail: [
      "Area centroids are recomputed as new contributions land so they track real activity, not a fixed grid.",
      "Per-area aggregations roll up totals, activity-mode share, and sensor diversity — the layer researchers actually consume.",
    ],
  },
  {
    icon: "science",
    title: "Deliver",
    body: "Dataset slices are scoped to a research project: geography, time window, sensors, and (optionally) activity mode. Slices are delivered as documented JSON or Parquet with reproducible schemas — every aggregation rule is version-controlled.",
    detail: [
      "Slice schemas are published; nothing is ever a black-box transform.",
      "When a slice is licensed, contributors whose data was included may receive a share, or direct it to environmental causes. This is the only place money enters the loop.",
    ],
  },
];

const PRIVACY_GUARANTEES = [
  {
    icon: "person_off",
    title: "No personal identifiers",
    body: "Anonymous Firebase Auth IDs. No email, no name, no phone number — ever. Nothing tying a UID to a real person sits on our servers.",
  },
  {
    icon: "mic_off",
    title: "No audio recordings",
    body: "The microphone reports only amplitude (how loud) — never an audio sample. Conversations and ambient sound content stay on the device.",
  },
  {
    icon: "location_off",
    title: "Location is bucketed",
    body: "Individual GPS tracks never leave the device unencoded. Researchers consume 250 km aggregates by default; finer slices require explicit, scoped delivery.",
  },
  {
    icon: "control_camera",
    title: "Contributor control",
    body: "Sessions are explicit start/stop. Nothing is collected unless the user has taken an action. Sensors can be toggled individually at any time.",
  },
];

export const HowWeCollect = () => {
  return (
    <>
      <DefaultNavbar routes={consumerRoutes} sticky />

      {/* Hero */}
      <MKBox
        component="section"
        sx={({ palette }) => ({
          background: `linear-gradient(180deg, ${palette.white.main} 0%, #EFF6FF 100%)`,
          pt: { xs: 12, md: 16 },
          pb: { xs: 8, md: 10 },
        })}
      >
        <Container>
          <Grid container justifyContent="center" textAlign="center">
            <Grid item xs={12} md={10} lg={9}>
              <MKTypography
                variant="overline"
                color="primary"
                fontWeight="bold"
                sx={{ letterSpacing: "0.1em" }}
              >
                Methodology
              </MKTypography>
              <MKTypography
                variant="h1"
                color="dark"
                mt={1.5}
                sx={({ breakpoints, typography: { size } }) => ({
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                  [breakpoints.down("md")]: { fontSize: size["3xl"] },
                })}
              >
                How we collect the data
              </MKTypography>
              <MKTypography
                variant="body1"
                color="text"
                mt={3}
                sx={{ maxWidth: 720, mx: "auto", fontSize: { xs: 16, md: 18 }, lineHeight: 1.55 }}
              >
                The end-to-end pipeline from a contributor&apos;s phone to a delivered research
                dataset — with the privacy boundaries that hold at every step.
              </MKTypography>
            </Grid>
          </Grid>
        </Container>
      </MKBox>

      {/* Pipeline stages */}
      <MKBox component="section" py={{ xs: 8, md: 12 }} px={{ xs: 2, md: 4 }}>
        <Container maxWidth="md">
          {STAGES.map((s, i) => (
            <MKBox
              key={s.title}
              sx={({ palette }) => ({
                py: { xs: 4, md: 5 },
                borderTop: i === 0 ? "none" : `1px solid ${palette.grey[200]}`,
              })}
            >
              <Grid container spacing={{ xs: 2, md: 4 }} alignItems="flex-start">
                <Grid item xs={12} md={3}>
                  <MKBox display="flex" alignItems="center" sx={{ gap: 2 }}>
                    <MKBox
                      sx={({ palette }) => ({
                        width: 44,
                        height: 44,
                        borderRadius: "10px",
                        display: "grid",
                        placeItems: "center",
                        backgroundColor: "rgba(37, 99, 255, 0.1)",
                        color: palette.primary.main,
                        flexShrink: 0,
                      })}
                    >
                      <Icon fontSize="small">{s.icon}</Icon>
                    </MKBox>
                    <MKBox>
                      <MKTypography
                        variant="overline"
                        color="primary"
                        fontWeight="bold"
                        sx={{ letterSpacing: "0.1em", display: "block", lineHeight: 1 }}
                      >
                        Stage {String(i + 1).padStart(2, "0")}
                      </MKTypography>
                      <MKTypography
                        variant="h5"
                        color="dark"
                        fontWeight="bold"
                        sx={{ mt: 0.5, letterSpacing: "-0.01em" }}
                      >
                        {s.title}
                      </MKTypography>
                    </MKBox>
                  </MKBox>
                </Grid>
                <Grid item xs={12} md={9}>
                  <MKTypography variant="body1" color="dark" sx={{ lineHeight: 1.65 }}>
                    {s.body}
                  </MKTypography>
                  <MKBox component="ul" sx={{ mt: 1.5, pl: 2.5, m: 0 }}>
                    {s.detail.map((d) => (
                      <MKBox
                        component="li"
                        key={d}
                        sx={({ palette }) => ({
                          color: palette.text.main,
                          fontSize: 14,
                          lineHeight: 1.6,
                          mt: 0.5,
                        })}
                      >
                        {d}
                      </MKBox>
                    ))}
                  </MKBox>
                </Grid>
              </Grid>
            </MKBox>
          ))}
        </Container>
      </MKBox>

      {/* Privacy guarantees */}
      <MKBox
        component="section"
        py={{ xs: 8, md: 12 }}
        px={{ xs: 2, md: 4 }}
        sx={({ palette }) => ({
          background: palette.dark.main,
          color: palette.white.main,
        })}
      >
        <Container>
          <Grid container justifyContent="center" textAlign="center" mb={{ xs: 5, md: 6 }}>
            <Grid item xs={12} md={8}>
              <MKTypography
                variant="overline"
                sx={({ palette }) => ({
                  color: palette.info.main,
                  letterSpacing: "0.1em",
                  fontWeight: 700,
                })}
              >
                Privacy guarantees
              </MKTypography>
              <MKTypography
                variant="h2"
                mt={1}
                sx={({ palette }) => ({
                  color: palette.white.main,
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.15,
                })}
              >
                What we never collect
              </MKTypography>
              <MKTypography
                variant="body1"
                mt={2}
                sx={({ palette }) => ({
                  color: palette.grey[300],
                  maxWidth: 640,
                  mx: "auto",
                })}
              >
                These boundaries are enforced in the device code, not the marketing copy. Open the
                Android app source to verify.
              </MKTypography>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            {PRIVACY_GUARANTEES.map((p) => (
              <Grid item xs={12} sm={6} md={3} key={p.title}>
                <MKBox
                  sx={{
                    p: 3,
                    height: "100%",
                    borderRadius: 3,
                    border: "1px solid rgba(255, 255, 255, 0.08)",
                    background: "rgba(255, 255, 255, 0.03)",
                  }}
                >
                  <MKBox
                    sx={({ palette }) => ({
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      display: "grid",
                      placeItems: "center",
                      background: "rgba(56, 189, 248, 0.15)",
                      color: palette.info.main,
                      mb: 2,
                    })}
                  >
                    <Icon fontSize="small">{p.icon}</Icon>
                  </MKBox>
                  <MKTypography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={({ palette }) => ({ color: palette.white.main })}
                  >
                    {p.title}
                  </MKTypography>
                  <MKTypography
                    variant="body2"
                    mt={1}
                    sx={({ palette }) => ({ color: palette.grey[400], lineHeight: 1.55 })}
                  >
                    {p.body}
                  </MKTypography>
                </MKBox>
              </Grid>
            ))}
          </Grid>
        </Container>
      </MKBox>

      <SiteFooter content={footerRoutes} />
    </>
  );
};
